import * as React from "react";
import { FileText, MessageSquare, MoreVertical, Pencil, Trash } from "lucide-react";
import { Link } from "react-router";
import { useToggle } from "react-use";

import { useDateFormatter } from "@/hooks/useDateFormatter";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RenameConversationModal } from "./RenameConversationModal";
import { DeleteConversationModal } from "./DeleteConversationModal";
import { RecentQAConversation } from "../types";

interface Props {
  conversation: RecentQAConversation;
}

export const RecentQARow: React.FC<Props> = ({ conversation }) => {
  const { formatDateTime } = useDateFormatter();
  const [isRenameOpen, toggleIsRenameOpen] = useToggle(false);
  const [isDeleteOpen, toggleIsDeleteOpen] = useToggle(false);

  const displayText = conversation.name ?? conversation.last_message;
  const truncatedMessage = displayText && displayText.length > 80 ? `${displayText.slice(0, 80)}...` : displayText;

  return (
    <>
      <Link
        to={`/chat/${conversation.fse_entry_id}/${conversation.conversation_id}`}
        className="group flex items-center rounded-lg border bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
      >
        <div className="flex flex-1 items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{conversation.document_name ?? "Unknown document"}</span>
            </div>
            {truncatedMessage && <p className="text-sm text-muted-foreground">{truncatedMessage}</p>}
            <p className="text-xs text-muted-foreground">{formatDateTime(conversation.updated_at)}</p>
          </div>
        </div>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 w-8 shrink-0 p-0 opacity-0 transition-opacity group-hover:opacity-100"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <MoreVertical size={16} className="text-gray-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => toggleIsRenameOpen(true)} className="cursor-pointer">
              <Pencil className="mr-2 h-4 w-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleIsDeleteOpen(true)} className="cursor-pointer">
              <Trash className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Link>

      <RenameConversationModal isOpen={isRenameOpen} setIsOpen={toggleIsRenameOpen} conversation={conversation} />
      <DeleteConversationModal isOpen={isDeleteOpen} setIsOpen={toggleIsDeleteOpen} conversation={conversation} />
    </>
  );
};
